import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import api from "@/lib/api";
import Pill from "@/components/Pill";
import { useRefData } from "@/context/RefDataContext";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { Loader2, ListChecks, AlertOctagon, ClipboardList, Plus, Settings2, Search, X, ChevronLeft, ChevronRight } from "lucide-react";
import { can } from "@/lib/permissions";
import { useAuth } from "@/context/AuthContext";
import RaiseEntityDrawer from "@/components/RaiseEntityDrawer";

function fmtDate(iso) {
  if (!iso) return "—";
  return new Date(iso).toLocaleString(undefined, { dateStyle: "medium" });
}

/** Absolute date + 24h time — used by the ticket list per product spec. */
function fmtDateTime24(iso) {
  if (!iso) return "—";
  const d = new Date(iso);
  const pad = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())} ${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

/** Human-friendly "open since" (elapsed between creation and now). */
function fmtElapsed(iso) {
  if (!iso) return "—";
  const then = new Date(iso).getTime();
  const now = Date.now();
  let s = Math.max(0, Math.floor((now - then) / 1000));
  const d = Math.floor(s / 86400); s -= d * 86400;
  const h = Math.floor(s / 3600);  s -= h * 3600;
  const m = Math.floor(s / 60);
  if (d > 0) return `${d}d ${h}h`;
  if (h > 0) return `${h}h ${m}m`;
  return `${m}m`;
}

function lookup(list, id) {
  return list?.find((x) => x.value === id)?.label || "—";
}

/** Look up a user's display name only (strips the "(email)" suffix used by
 *  RefDataContext's users list). Returns em-dash when not found / unassigned. */
function lookupUserName(list, id) {
  if (!id) return "—";
  const hit = list?.find((x) => x.value === id);
  if (!hit) return "—";
  return hit.name || (hit.label || "").replace(/\s*\([^)]*\)\s*$/, "") || "—";
}

// -------------------- Ticket List (with search, filters, column picker)
const TICKET_COLUMNS = [
  { key: "code",         label: "Ticket ID",       default: true },
  { key: "subject",      label: "Subject",         default: true },
  { key: "cdark",        label: "CDARK",           default: true },
  { key: "asset",        label: "Related Asset",   default: true },
  { key: "service",      label: "Related Service", default: true },
  { key: "team",         label: "Team",            default: true },
  { key: "assigned_to",  label: "Assigned To",     default: true },
  { key: "priority",     label: "Priority",        default: true },
  { key: "status",       label: "Status",          default: true },
  { key: "created_at",   label: "Created On",      default: true },
  { key: "last_reply",   label: "Last Reply",      default: true },
  { key: "open_since",   label: "Open Since",      default: true },
  { key: "closed_at",    label: "Closed On",       default: true },
];
const HIDABLE_STATUSES = ["On Hold", "Resolved", "Verified", "Closed"];
const PAGE_SIZE_OPTIONS = [5, 10, 25, 50, 100];
const DEFAULT_PAGE_SIZE = 5;

// -------------------- Task List columns (same UX pattern as tickets)
const TASK_HIDABLE_STATUSES = ["On Hold", "Completed", "Closed"];
const TASK_COLUMNS = [
  { key: "code",         label: "Task ID",     default: true },
  { key: "subject",      label: "Subject",     default: true },
  { key: "cdark",        label: "CDARK",       default: true },
  { key: "team",         label: "Team",        default: true },
  { key: "assigned_to",  label: "Assigned To", default: true },
  { key: "priority",     label: "Priority",    default: true },
  { key: "status",       label: "Status",      default: true },
  { key: "due",          label: "Due",         default: true },
  { key: "created_at",   label: "Created On",  default: true },
];

function loadTaskColPrefs(scope) {
  try {
    const raw = localStorage.getItem(`assign.task.cols.${scope}`);
    if (!raw) return null;
    return JSON.parse(raw);
  } catch (_) {
    return null;
  }
}
function saveTaskColPrefs(scope, cols) {
  try {
    localStorage.setItem(`assign.task.cols.${scope}`, JSON.stringify(cols));
  } catch (_) {
    // ignore quota / private-mode errors — column prefs are non-critical.
  }
}

function loadColPrefs(scope) {
  try {
    const raw = localStorage.getItem(`assign.ticket.cols.${scope}`);
    if (!raw) return null;
    return JSON.parse(raw);
  } catch (_) {
    return null;
  }
}
function saveColPrefs(scope, cols) {
  try {
    localStorage.setItem(`assign.ticket.cols.${scope}`, JSON.stringify(cols));
  } catch (_) {
    // ignore quota / private-mode errors — column prefs are non-critical.
  }
}

function TicketSection({ title, rows, refData, loading, scope }) {
  const [search, setSearch] = useState("");
  const [statusToggles, setStatusToggles] = useState({});
  const [colOpen, setColOpen] = useState(false);
  // Client-side pagination — default 5 tickets per page. `page` is 0-indexed
  // and gets reset to 0 whenever the underlying filter set changes.
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);
  const [page, setPage] = useState(0);
  // Which columns are visible, keyed by TICKET_COLUMNS[i].key. Loaded once
  // from localStorage per-scope so the user's layout survives reloads. New
  // columns added to TICKET_COLUMNS after the prefs were saved fall back to
  // their `default` so returning users see them without a manual reset.
  const initialCols = useMemo(() => {
    const stored = loadColPrefs(scope) || {};
    const merged = {};
    for (const c of TICKET_COLUMNS) {
      merged[c.key] = Object.prototype.hasOwnProperty.call(stored, c.key)
        ? stored[c.key]
        : c.default;
    }
    return merged;
  }, [scope]);
  const [visibleCols, setVisibleCols] = useState(initialCols);
  const persistCols = useRef(false);
  useEffect(() => { if (persistCols.current) saveColPrefs(scope, visibleCols); persistCols.current = true; }, [visibleCols, scope]);

  // Filter pipeline:
  //   • No capsule active → show only non-hidable statuses (Open / In Progress).
  //   • One or more capsules active → show ONLY tickets whose status matches
  //     an active capsule (exclusive filter — hides everything else).
  //   • Search runs on top of whichever set survives.
  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    const activeStatuses = HIDABLE_STATUSES.filter((s) => statusToggles[s]);
    const exclusive = activeStatuses.length > 0;
    return rows.filter((r) => {
      const status = r.status || "Open";
      if (exclusive) {
        if (!activeStatuses.includes(status)) return false;
      } else if (HIDABLE_STATUSES.includes(status)) {
        return false;
      }
      if (!q) return true;
      const hay = [
        r.code, r.subject, r.status, r.priority,
        lookup(refData.projects, r.project_id),
        lookup(refData.assets, r.asset_id),
        lookup(refData.services, r.service_id),
        lookup(refData.teams, r.team_id),
        lookupUserName(refData.users, r.assigned_user_id),
      ].filter(Boolean).join(" ").toLowerCase();
      return hay.includes(q);
    });
  }, [rows, statusToggles, search, refData]);

  const shownCols = TICKET_COLUMNS.filter((c) => visibleCols[c.key]);
  const colCount = shownCols.length || 1;

  // Pagination — clamp `page` to the last available page whenever `filtered`
  // shrinks (e.g. user typed a search that reduces results below the current
  // offset). Slice the visible window from the filtered list.
  const totalPages = Math.max(1, Math.ceil(filtered.length / pageSize));
  const safePage = Math.min(page, totalPages - 1);
  useEffect(() => { if (page !== safePage) setPage(safePage); }, [page, safePage]);
  // Reset to page 1 whenever the filter set changes (search / capsules /
  // page-size). The row data itself changing (e.g. background refetch)
  // preserves the current page.
  useEffect(() => { setPage(0); }, [search, statusToggles, pageSize]);
  const pageStart = safePage * pageSize;
  const pageEnd = pageStart + pageSize;
  const paged = filtered.slice(pageStart, pageEnd);
  const firstRowIndex = filtered.length === 0 ? 0 : pageStart + 1;
  const lastRowIndex = Math.min(pageEnd, filtered.length);
  const capsule = (label, status) => (
    <button
      key={status}
      type="button"
      onClick={() => setStatusToggles((s) => ({ ...s, [status]: !s[status] }))}
      className={`text-xs px-3 py-1 rounded-full border transition-colors ${
        statusToggles[status]
          ? "bg-sky-500/20 border-sky-500/40 text-sky-200"
          : "bg-zinc-900/60 border-white/10 text-zinc-400 hover:border-white/20 hover:text-zinc-200"
      }`}
      data-testid={`ticket-filter-${status.toLowerCase().replace(/\s+/g, '-')}`}
    >
      {label}
    </button>
  );

  return (
    <section className="space-y-3" data-testid={`assignments-section-${scope}-ticket`}>
      <div className="flex items-center gap-2 flex-wrap">
        <AlertOctagon className="w-4 h-4 text-zinc-400" />
        <h2 className="text-sm font-semibold tracking-wide uppercase text-zinc-300">{title}</h2>
        <span className="text-xs text-zinc-500">({filtered.length}/{rows.length})</span>
      </div>

      <div className="flex items-center gap-2 flex-wrap">
        <div className="relative flex-1 min-w-[220px] max-w-md">
          <Search className="w-3.5 h-3.5 absolute left-3 top-1/2 -translate-y-1/2 text-zinc-500" />
          <Input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search ID, subject, CDARK, team, assignee…"
            className="bg-zinc-900 border-white/10 text-white pl-9 pr-8 h-9"
            data-testid={`ticket-search-${scope}`}
          />
          {search && (
            <button
              type="button"
              onClick={() => setSearch("")}
              className="absolute right-2 top-1/2 -translate-y-1/2 text-zinc-500 hover:text-white"
              aria-label="Clear search"
            >
              <X className="w-3.5 h-3.5" />
            </button>
          )}
        </div>
        {capsule("Show On Hold", "On Hold")}
        {capsule("Show Resolved", "Resolved")}
        {capsule("Show Verified", "Verified")}
        {capsule("Show Closed", "Closed")}
        <div className="ml-auto">
          <Popover open={colOpen} onOpenChange={setColOpen}>
            <PopoverTrigger asChild>
              <Button
                variant="ghost"
                size="sm"
                className="h-9 w-9 p-0 text-zinc-400 hover:text-white hover:bg-white/5"
                data-testid={`ticket-columns-btn-${scope}`}
                title="Choose columns"
              >
                <Settings2 className="w-4 h-4" />
              </Button>
            </PopoverTrigger>
            <PopoverContent align="end" className="w-56 bg-zinc-950 border-white/10 text-zinc-100" data-testid={`ticket-columns-popover-${scope}`}>
              <div className="text-[11px] uppercase tracking-wider text-zinc-500 mb-2">Columns</div>
              <div className="space-y-1.5">
                {TICKET_COLUMNS.map((c) => (
                  <label key={c.key} className="flex items-center gap-2 cursor-pointer text-sm text-zinc-200 hover:text-white">
                    <input
                      type="checkbox"
                      checked={!!visibleCols[c.key]}
                      onChange={() => setVisibleCols((v) => ({ ...v, [c.key]: !v[c.key] }))}
                      className="accent-sky-400 w-3.5 h-3.5"
                      data-testid={`ticket-column-toggle-${scope}-${c.key}`}
                    />
                    {c.label}
                  </label>
                ))}
              </div>
            </PopoverContent>
          </Popover>
        </div>
      </div>

      {/* Pagination bar — sits directly under the capsule filter row. Users
          can change the page size (default 5) and jump prev/next. Hidden
          only when there is literally nothing to show; otherwise it renders
          disabled controls so the layout doesn't jump around. */}
      <div className="flex items-center justify-between gap-3 flex-wrap px-1">
        <div className="flex items-center gap-2 text-xs text-zinc-400">
          <span>Show</span>
          <select
            value={pageSize}
            onChange={(e) => setPageSize(Number(e.target.value))}
            className="bg-zinc-900 border border-white/10 rounded-md h-8 px-2 text-white text-xs focus:outline-none focus:ring-1 focus:ring-sky-500/40 cursor-pointer"
            data-testid={`ticket-page-size-${scope}`}
            aria-label="Rows per page"
          >
            {PAGE_SIZE_OPTIONS.map((n) => (
              <option key={n} value={n}>{n}</option>
            ))}
          </select>
          <span>per page</span>
          <span className="ml-2 text-zinc-500">
            {filtered.length === 0
              ? "0 tickets"
              : `${firstRowIndex}–${lastRowIndex} of ${filtered.length}`}
          </span>
        </div>
        <div className="flex items-center gap-2">
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={() => setPage((p) => Math.max(0, p - 1))}
            disabled={safePage <= 0 || loading}
            className="h-8 px-2 text-zinc-300 hover:text-white hover:bg-white/5 disabled:opacity-40 disabled:hover:bg-transparent"
            data-testid={`ticket-page-prev-${scope}`}
          >
            <ChevronLeft className="w-3.5 h-3.5 mr-1" /> Prev
          </Button>
          <span className="text-xs text-zinc-500 font-mono tabular-nums" data-testid={`ticket-page-indicator-${scope}`}>
            Page {safePage + 1} / {totalPages}
          </span>
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={() => setPage((p) => Math.min(totalPages - 1, p + 1))}
            disabled={safePage >= totalPages - 1 || loading}
            className="h-8 px-2 text-zinc-300 hover:text-white hover:bg-white/5 disabled:opacity-40 disabled:hover:bg-transparent"
            data-testid={`ticket-page-next-${scope}`}
          >
            Next <ChevronRight className="w-3.5 h-3.5 ml-1" />
          </Button>
        </div>
      </div>

      <div className="w-full overflow-hidden border border-white/10 rounded-lg bg-zinc-900">
        <table className="w-full text-sm">
          <thead>
            <tr className="bg-zinc-950/50 border-b border-white/10 text-left">
              {shownCols.map((c) => (
                <th key={c.key} className="px-4 py-3 text-[11px] uppercase tracking-wider text-zinc-500 font-medium">{c.label}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={colCount} className="px-4 py-10 text-center text-zinc-500 text-sm">
                <Loader2 className="w-4 h-4 animate-spin inline-block mr-2" /> Loading…
              </td></tr>
            ) : filtered.length === 0 ? (
              <tr><td colSpan={colCount} className="px-4 py-12 text-center text-zinc-500 text-sm">
                {rows.length === 0 ? "Nothing here yet." : "No tickets match the current filters."}
              </td></tr>
            ) : paged.map((r) => (
              <tr key={r.id} className="border-b border-white/5 hover:bg-white/[0.025] transition-colors" data-testid={`assignments-row-${r.id}`}>
                {visibleCols.code && (
                  <td className="px-4 py-3">
                    <Link to={`/issues/${r.id}`} className="font-mono text-xs text-sky-400 hover:underline">{r.code}</Link>
                  </td>
                )}
                {visibleCols.subject && (
                  <td className="px-4 py-3">
                    <Link to={`/issues/${r.id}`} className="text-white hover:text-sky-300 transition-colors">{r.subject}</Link>
                  </td>
                )}
                {visibleCols.cdark && <td className="px-4 py-3"><span className="text-zinc-400 text-xs">{lookup(refData.projects, r.project_id)}</span></td>}
                {visibleCols.asset && <td className="px-4 py-3"><span className="text-zinc-400 text-xs">{lookup(refData.assets, r.asset_id)}</span></td>}
                {visibleCols.service && <td className="px-4 py-3"><span className="text-zinc-400 text-xs">{lookup(refData.services, r.service_id)}</span></td>}
                {visibleCols.team && <td className="px-4 py-3"><span className="text-zinc-400 text-xs">{lookup(refData.teams, r.team_id)}</span></td>}
                {visibleCols.assigned_to && <td className="px-4 py-3"><span className="text-zinc-300 text-xs">{lookupUserName(refData.users, r.assigned_user_id)}</span></td>}
                {visibleCols.priority && <td className="px-4 py-3"><Pill kind="priority" value={r.priority} /></td>}
                {visibleCols.status && <td className="px-4 py-3"><Pill kind="status" value={r.status} /></td>}
                {visibleCols.created_at && <td className="px-4 py-3 text-zinc-500 text-xs font-mono">{fmtDateTime24(r.created_at)}</td>}
                {visibleCols.last_reply && <td className="px-4 py-3 text-zinc-500 text-xs font-mono">{fmtDateTime24(r.last_reply_at)}</td>}
                {visibleCols.open_since && <td className="px-4 py-3 text-zinc-400 text-xs">{fmtElapsed(r.created_at)}</td>}
                {visibleCols.closed_at && <td className="px-4 py-3 text-zinc-500 text-xs font-mono">{r.status === "Closed" ? fmtDateTime24(r.closed_at) : "—"}</td>}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </section>
  );
}

function TaskSection({ title, rows, refData, loading, scope }) {
  // Mirrors TicketSection's UX (search + pagination + column picker) plus
  // capsule filters for the "hidable" task statuses. Exclusive semantics:
  //   • No capsule active → hide every hidable status (default view).
  //   • Any capsules active → show ONLY tasks whose status matches an
  //     active capsule.
  const [search, setSearch] = useState("");
  const [statusToggles, setStatusToggles] = useState({});
  const [colOpen, setColOpen] = useState(false);
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);
  const [page, setPage] = useState(0);

  const initialCols = useMemo(() => {
    const stored = loadTaskColPrefs(scope) || {};
    const merged = {};
    for (const c of TASK_COLUMNS) {
      merged[c.key] = Object.prototype.hasOwnProperty.call(stored, c.key)
        ? stored[c.key]
        : c.default;
    }
    return merged;
  }, [scope]);
  const [visibleCols, setVisibleCols] = useState(initialCols);
  const persistCols = useRef(false);
  useEffect(() => {
    if (persistCols.current) saveTaskColPrefs(scope, visibleCols);
    persistCols.current = true;
  }, [visibleCols, scope]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    const activeStatuses = TASK_HIDABLE_STATUSES.filter((s) => statusToggles[s]);
    const exclusive = activeStatuses.length > 0;
    return rows.filter((r) => {
      const status = r.status || "Open";
      if (exclusive) {
        if (!activeStatuses.includes(status)) return false;
      } else if (TASK_HIDABLE_STATUSES.includes(status)) {
        return false;
      }
      if (!q) return true;
      const hay = [
        r.code, r.subject, r.status, r.priority, r.due_date,
        lookup(refData.projects, r.project_id),
        lookup(refData.teams, r.team_id),
        lookupUserName(refData.users, r.assigned_user_id),
      ].filter(Boolean).join(" ").toLowerCase();
      return hay.includes(q);
    });
  }, [rows, search, statusToggles, refData]);

  const shownCols = TASK_COLUMNS.filter((c) => visibleCols[c.key]);
  const colCount = shownCols.length || 1;

  const totalPages = Math.max(1, Math.ceil(filtered.length / pageSize));
  const safePage = Math.min(page, totalPages - 1);
  useEffect(() => { if (page !== safePage) setPage(safePage); }, [page, safePage]);
  useEffect(() => { setPage(0); }, [search, statusToggles, pageSize]);
  const pageStart = safePage * pageSize;
  const pageEnd = pageStart + pageSize;
  const paged = filtered.slice(pageStart, pageEnd);
  const firstRowIndex = filtered.length === 0 ? 0 : pageStart + 1;
  const lastRowIndex = Math.min(pageEnd, filtered.length);

  return (
    <section className="space-y-3" data-testid={`assignments-section-${scope}-task`}>
      <div className="flex items-center gap-2 flex-wrap">
        <ListChecks className="w-4 h-4 text-zinc-400" />
        <h2 className="text-sm font-semibold tracking-wide uppercase text-zinc-300">{title}</h2>
        <span className="text-xs text-zinc-500">({filtered.length}/{rows.length})</span>
      </div>

      <div className="flex items-center gap-2 flex-wrap">
        <div className="relative flex-1 min-w-[220px] max-w-md">
          <Search className="w-3.5 h-3.5 absolute left-3 top-1/2 -translate-y-1/2 text-zinc-500" />
          <Input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search ID, subject, CDARK, team, assignee…"
            className="bg-zinc-900 border-white/10 text-white pl-9 pr-8 h-9"
            data-testid={`task-search-${scope}`}
          />
          {search && (
            <button
              type="button"
              onClick={() => setSearch("")}
              className="absolute right-2 top-1/2 -translate-y-1/2 text-zinc-500 hover:text-white"
              aria-label="Clear search"
            >
              <X className="w-3.5 h-3.5" />
            </button>
          )}
        </div>
        <button
          type="button"
          onClick={() => setStatusToggles((t) => ({ ...t, "On Hold": !t["On Hold"] }))}
          className={`text-xs px-3 py-1 rounded-full border transition-colors ${
            statusToggles["On Hold"]
              ? "bg-sky-500/15 border-sky-500/40 text-sky-300"
              : "bg-zinc-900 border-white/10 text-zinc-400 hover:text-white hover:border-white/20"
          }`}
          data-testid={`task-filter-on-hold-${scope}`}
        >
          Show On Hold
        </button>
        <button
          type="button"
          onClick={() => setStatusToggles((t) => ({ ...t, Completed: !t.Completed }))}
          className={`text-xs px-3 py-1 rounded-full border transition-colors ${
            statusToggles.Completed
              ? "bg-sky-500/15 border-sky-500/40 text-sky-300"
              : "bg-zinc-900 border-white/10 text-zinc-400 hover:text-white hover:border-white/20"
          }`}
          data-testid={`task-filter-completed-${scope}`}
        >
          Show Completed
        </button>
        <button
          type="button"
          onClick={() => setStatusToggles((t) => ({ ...t, Closed: !t.Closed }))}
          className={`text-xs px-3 py-1 rounded-full border transition-colors ${
            statusToggles.Closed
              ? "bg-sky-500/15 border-sky-500/40 text-sky-300"
              : "bg-zinc-900 border-white/10 text-zinc-400 hover:text-white hover:border-white/20"
          }`}
          data-testid={`task-filter-closed-${scope}`}
        >
          Show Closed
        </button>
        <div className="ml-auto">
          <Popover open={colOpen} onOpenChange={setColOpen}>
            <PopoverTrigger asChild>
              <Button
                variant="ghost"
                size="sm"
                className="h-9 w-9 p-0 text-zinc-400 hover:text-white hover:bg-white/5"
                data-testid={`task-columns-btn-${scope}`}
                title="Choose columns"
              >
                <Settings2 className="w-4 h-4" />
              </Button>
            </PopoverTrigger>
            <PopoverContent align="end" className="w-56 bg-zinc-950 border-white/10 text-zinc-100" data-testid={`task-columns-popover-${scope}`}>
              <div className="text-[11px] uppercase tracking-wider text-zinc-500 mb-2">Columns</div>
              <div className="space-y-1.5">
                {TASK_COLUMNS.map((c) => (
                  <label key={c.key} className="flex items-center gap-2 cursor-pointer text-sm text-zinc-200 hover:text-white">
                    <input
                      type="checkbox"
                      checked={!!visibleCols[c.key]}
                      onChange={() => setVisibleCols((v) => ({ ...v, [c.key]: !v[c.key] }))}
                      className="accent-sky-400 w-3.5 h-3.5"
                      data-testid={`task-column-toggle-${scope}-${c.key}`}
                    />
                    {c.label}
                  </label>
                ))}
              </div>
            </PopoverContent>
          </Popover>
        </div>
      </div>

      {/* Pagination bar */}
      <div className="flex items-center justify-between gap-3 flex-wrap px-1">
        <div className="flex items-center gap-2 text-xs text-zinc-400">
          <span>Show</span>
          <select
            value={pageSize}
            onChange={(e) => setPageSize(Number(e.target.value))}
            className="bg-zinc-900 border border-white/10 rounded-md h-8 px-2 text-white text-xs focus:outline-none focus:ring-1 focus:ring-sky-500/40 cursor-pointer"
            data-testid={`task-page-size-${scope}`}
            aria-label="Rows per page"
          >
            {PAGE_SIZE_OPTIONS.map((n) => (
              <option key={n} value={n}>{n}</option>
            ))}
          </select>
          <span>per page</span>
          <span className="ml-2 text-zinc-500">
            {filtered.length === 0
              ? "0 tasks"
              : `${firstRowIndex}–${lastRowIndex} of ${filtered.length}`}
          </span>
        </div>
        <div className="flex items-center gap-2">
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={() => setPage((p) => Math.max(0, p - 1))}
            disabled={safePage <= 0 || loading}
            className="h-8 px-2 text-zinc-300 hover:text-white hover:bg-white/5 disabled:opacity-40 disabled:hover:bg-transparent"
            data-testid={`task-page-prev-${scope}`}
          >
            <ChevronLeft className="w-3.5 h-3.5 mr-1" /> Prev
          </Button>
          <span className="text-xs text-zinc-500 font-mono tabular-nums" data-testid={`task-page-indicator-${scope}`}>
            Page {safePage + 1} / {totalPages}
          </span>
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={() => setPage((p) => Math.min(totalPages - 1, p + 1))}
            disabled={safePage >= totalPages - 1 || loading}
            className="h-8 px-2 text-zinc-300 hover:text-white hover:bg-white/5 disabled:opacity-40 disabled:hover:bg-transparent"
            data-testid={`task-page-next-${scope}`}
          >
            Next <ChevronRight className="w-3.5 h-3.5 ml-1" />
          </Button>
        </div>
      </div>

      <div className="w-full overflow-hidden border border-white/10 rounded-lg bg-zinc-900">
        <table className="w-full text-sm">
          <thead>
            <tr className="bg-zinc-950/50 border-b border-white/10 text-left">
              {shownCols.map((c) => (
                <th key={c.key} className="px-4 py-3 text-[11px] uppercase tracking-wider text-zinc-500 font-medium">{c.label}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={colCount} className="px-4 py-10 text-center text-zinc-500 text-sm">
                <Loader2 className="w-4 h-4 animate-spin inline-block mr-2" /> Loading…
              </td></tr>
            ) : filtered.length === 0 ? (
              <tr><td colSpan={colCount} className="px-4 py-12 text-center text-zinc-500 text-sm">
                {rows.length === 0 ? "Nothing here yet." : "No tasks match the current filters."}
              </td></tr>
            ) : paged.map((r) => (
              <tr key={r.id} className="border-b border-white/5 hover:bg-white/[0.025] transition-colors" data-testid={`assignments-row-${r.id}`}>
                {visibleCols.code && (
                  <td className="px-4 py-3">
                    <Link to={`/tasks/${r.id}`} className="font-mono text-xs text-sky-400 hover:underline">{r.code}</Link>
                  </td>
                )}
                {visibleCols.subject && (
                  <td className="px-4 py-3">
                    <Link to={`/tasks/${r.id}`} className="text-white hover:text-sky-300 transition-colors">{r.subject}</Link>
                  </td>
                )}
                {visibleCols.cdark && <td className="px-4 py-3"><span className="text-zinc-400 text-xs">{lookup(refData.projects, r.project_id)}</span></td>}
                {visibleCols.team && <td className="px-4 py-3"><span className="text-zinc-400 text-xs">{lookup(refData.teams, r.team_id)}</span></td>}
                {visibleCols.assigned_to && <td className="px-4 py-3"><span className="text-zinc-300 text-xs">{lookupUserName(refData.users, r.assigned_user_id)}</span></td>}
                {visibleCols.priority && <td className="px-4 py-3"><Pill kind="priority" value={r.priority} /></td>}
                {visibleCols.status && <td className="px-4 py-3"><Pill kind="status" value={r.status} /></td>}
                {visibleCols.due && <td className="px-4 py-3 text-zinc-500 text-xs">{r.due_date || "—"}</td>}
                {visibleCols.created_at && <td className="px-4 py-3 text-zinc-500 text-xs font-mono">{fmtDateTime24(r.created_at)}</td>}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </section>
  );
}

function AssignmentList({ scope, reloadKey, showTasks, showIssues }) {
  const refData = useRefData();
  const [tasks, setTasks] = useState([]);
  const [issues, setIssues] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const calls = [];
      calls.push(showTasks ? api.get("/tasks", { params: { scope } }) : Promise.resolve({ data: [] }));
      calls.push(showIssues ? api.get("/issues", { params: { scope } }) : Promise.resolve({ data: [] }));
      const [t, i] = await Promise.all(calls);
      setTasks(t.data);
      setIssues(i.data);
    } finally {
      setLoading(false);
    }
  }, [scope, showTasks, showIssues]);

  useEffect(() => { load(); }, [load, reloadKey]);

  let labels;
  if (scope === "my") labels = { tickets: "My Tickets", tasks: "My Tasks" };
  else if (scope === "team") labels = { tickets: "Team Tickets", tasks: "Team Tasks" };
  else labels = { tickets: "Other Tickets", tasks: "Other Tasks" };

  if (!showTasks && !showIssues) {
    return (
      <div className="text-center py-12 text-zinc-500 text-sm" data-testid="assignments-no-modules">
        Neither Tasks nor Tickets module is enabled for this view. Contact your admin.
      </div>
    );
  }

  return (
    <div className="space-y-8">
      {showIssues && <TicketSection title={labels.tickets} rows={issues} refData={refData} loading={loading} scope={scope} />}
      {showTasks && <TaskSection title={labels.tasks} rows={tasks} refData={refData} loading={loading} scope={scope} />}
    </div>
  );
}

export default function AssignmentsPage() {
  const [params, setParams] = useSearchParams();
  const requested = params.get("scope");
  const [tab, setTab] = useState(["team", "all_teams"].includes(requested) ? requested : "my");
  const [raiseTask, setRaiseTask] = useState(false);
  const [raiseTicket, setRaiseTicket] = useState(false);
  const [reloadKey, setReloadKey] = useState(0);
  const [modules, setModules] = useState([]);
  const [myTeamIds, setMyTeamIds] = useState([]);
  const refData = useRefData();
  const { user } = useAuth();

  useEffect(() => {
    let aborted = false;
    api.get("/me/modules").then(({ data }) => {
      if (!aborted) {
        setModules(data.modules || []);
        setMyTeamIds(data.team_ids || []);
      }
    }).catch(() => {});
    return () => { aborted = true; };
  }, []);

  const hasTasks = modules.includes("tasks");
  const hasIssues = modules.includes("issues");
  // Sub-permissions only count when their parent module is enabled.
  // Team Assignments tab → at least one of tasks/issues + the matching view_teammates sub.
  const showTeamTab =
    (hasTasks && modules.includes("tasks_view_teammates")) ||
    (hasIssues && modules.includes("issues_view_teammates"));
  // Other Assignments tab → same pattern with view_others (legacy keys fall back).
  const hasAllTeamsTasks =
    hasTasks && (modules.includes("tasks_view_others") || modules.includes("all_teams_tasks"));
  const hasAllTeamsTickets =
    hasIssues && (modules.includes("issues_view_others") || modules.includes("all_teams_tickets"));
  const showAllTeamsTab = hasAllTeamsTasks || hasAllTeamsTickets;

  // If the current tab becomes unavailable after permissions update, fall back.
  useEffect(() => {
    if (tab === "team" && !showTeamTab) setTab("my");
    if (tab === "all_teams" && !showAllTeamsTab) setTab("my");
  }, [showTeamTab, showAllTeamsTab, tab]);

  const canCreateTask = can(user, "tasks", "create") && hasTasks;
  const canCreateTicket = can(user, "issues", "create") && hasIssues;

  const onChange = (v) => {
    setTab(v);
    setParams({ scope: v }, { replace: true });
  };

  const onCreated = () => setReloadKey((k) => k + 1);

  return (
    <div className="space-y-6" data-testid="assignments-page">
      <header className="flex items-start justify-between flex-wrap gap-4">
        <div>
          <h1 className="text-3xl font-semibold tracking-tight text-white flex items-center gap-3" style={{ fontFamily: "Outfit" }}>
            <ClipboardList className="w-7 h-7 text-sky-400" /> Assignments
          </h1>
          <p className="text-sm text-zinc-500 mt-1">Tickets and Tasks routed to you or to your team(s). New items land in the Team Lead&apos;s My-tab until delegated.</p>
        </div>
        <div className="flex items-center gap-2">
          {canCreateTask && (
            <Button
              onClick={() => setRaiseTask(true)}
              className="bg-sky-500 text-black hover:bg-sky-400 font-medium"
              data-testid="assignments-raise-task"
            >
              <Plus className="w-4 h-4 mr-1.5" /> Raise Task
            </Button>
          )}
          {canCreateTicket && (
            <Button
              onClick={() => setRaiseTicket(true)}
              className="bg-fuchsia-500/90 text-black hover:bg-fuchsia-400 font-medium"
              data-testid="assignments-raise-ticket"
            >
              <Plus className="w-4 h-4 mr-1.5" /> Raise Ticket
            </Button>
          )}
        </div>
      </header>

      <Tabs value={tab} onValueChange={onChange}>
        <TabsList className="bg-zinc-900 border border-white/10">
          <TabsTrigger value="my" data-testid="assignments-tab-my" className="data-[state=active]:bg-white data-[state=active]:text-black">My Assignments</TabsTrigger>
          {showTeamTab && (
            <TabsTrigger value="team" data-testid="assignments-tab-team" className="data-[state=active]:bg-white data-[state=active]:text-black">Team Assignments</TabsTrigger>
          )}
          {showAllTeamsTab && (
            <TabsTrigger value="all_teams" data-testid="assignments-tab-all-teams" className="data-[state=active]:bg-white data-[state=active]:text-black">Other Assignments</TabsTrigger>
          )}
        </TabsList>
        <TabsContent value="my" className="mt-6">
          {tab === "my" && (
            <AssignmentList scope="my" reloadKey={reloadKey} showTasks={hasTasks} showIssues={hasIssues} />
          )}
        </TabsContent>
        <TabsContent value="team" className="mt-6">
          {tab === "team" && showTeamTab && (
            <AssignmentList scope="team" reloadKey={reloadKey} showTasks={hasTasks} showIssues={hasIssues} />
          )}
        </TabsContent>
        {showAllTeamsTab && (
          <TabsContent value="all_teams" className="mt-6">
            {tab === "all_teams" && (
              <AssignmentList
                scope="all_teams"
                reloadKey={reloadKey}
                showTasks={hasAllTeamsTasks}
                showIssues={hasAllTeamsTickets}
              />
            )}
          </TabsContent>
        )}
      </Tabs>

      <RaiseEntityDrawer
        open={raiseTask}
        onOpenChange={setRaiseTask}
        kind="task"
        referenceData={refData}
        myTeamIds={myTeamIds}
        onCreated={onCreated}
      />
      <RaiseEntityDrawer
        open={raiseTicket}
        onOpenChange={setRaiseTicket}
        kind="ticket"
        referenceData={refData}
        myTeamIds={myTeamIds}
        onCreated={onCreated}
      />
    </div>
  );
}
